/**
 * What the AI proposed for each line, that the purchaser has not yet acted on.
 *
 * ---- Why anything is outstanding at all ----
 *
 * The extraction's answer does not always survive the write. `verifyItemCodes` blanks a code the
 * catalogue does not have; `verifyUoms` blanks a unit the master does not recognise; and
 * `features/mail/createEnquiryLines.ts` blanks `item_code` outright whenever it has to substitute
 * a placeholder UOM, so a placeholder can never hide inside a finished-looking line. Each of those
 * is the right call on its own, and each leaves the purchaser re-deriving something the model
 * already worked out. This module finds those gaps.
 *
 * ---- The pairing rule, and why it is verified rather than trusted ----
 *
 * `ExtractedLine` carries no row number. The server numbers persisted rows 1..n in the order they
 * were sent (measured), so extraction line `i` corresponds to `enquiry_row_no === String(i + 1)`.
 * `enquiry_row_no` does NOT renumber when a row is deleted (also measured: a 3-line enquiry with
 * the middle row removed reads back as "1" and "3"), so survivors keep their pairing.
 *
 * That is an assumption about someone else's numbering, and a wrong pairing here would offer one
 * line's item code as a one-click apply on a different line. So `textsCorrespond` checks the two
 * still describe the same thing before any suggestion is offered. Dropping a real suggestion costs
 * a manual match; offering the wrong one costs a wrong order.
 *
 * Never pair on `item_code` or the child docname: the code is mutable and non-unique (the
 * duplicate-item case is why the through-key exists), and the docname is REGENERATED on every
 * wholesale replace (measured: `kfgaq7u14p` → `kfpuufeg3p` for the same logical row).
 */

import type { ExtractedLine } from '../../lib/api/useMailExtraction'
import type { EnquiryItem } from '../../lib/types'
import type { ItemMatch } from '../../lib/searchMatch'

export interface LineSuggestion {
  /** The model's code, when the persisted line has none. `null` when there is nothing to offer. */
  itemCode: string | null
  /**
   * True when that code is one the catalogue REJECTED. It must be shown but never applied in one
   * click — an unknown Link fails the whole document on save, not just its row. The UI opens the
   * catalogue picker pre-filled with it instead.
   */
  itemCodeRejected: boolean
  /**
   * Ranked alternatives from `bosun-search`, best first — empty unless that service answered.
   *
   * Unlike `itemCode` these are always safe to offer as one-click applies: every one is an
   * `item_code` the service returned from the catalogue itself, so none can be the unknown-Link
   * case `itemCodeRejected` exists for. Carried for MATCHED lines too — the second-best is what a
   * purchaser checks a borderline match against.
   */
  candidates: { item_code: string; item_name: string; score: number | null }[]
  /**
   * True when `itemCode` is the matcher's top hit and it scored below `AUTO_APPLY_SCORE`.
   *
   * `searchMatch.ts` no longer withholds a weak top match, so without this flag a 0.11 guess and
   * a 0.92 certainty would render identically and apply identically. It describes the PROPOSED
   * code's provenance only: a code the customer actually wrote is never flagged, however unsure
   * the matcher was about the same line.
   */
  matchLowConfidence: boolean
  /**
   * The customer's original unit, when the persisted one is our placeholder — DISPLAY ONLY, never
   * a one-click apply. `raw_uom` is populated by `verifyUoms` precisely when the value could NOT
   * be verified against the `UOM` master, so it is, by construction, not something `Enquiry
   * Item.uom` (a Frappe Link) can ever accept — writing it always fails
   * (`Could not find Row #1: UOM: NO`, see `lib/uom.ts`'s header). It is still worth showing: the
   * purchaser reads what the customer actually wrote while picking a real master UOM by hand.
   * Named `customerUom`, not `uom`, so nothing mistakes it for applicable.
   */
  customerUom: string | null
}

/** `verifyItemCodes` marks a code it could not verify by appending this to the description. */
const REJECTED_MARKER = /\(code (.+?) not in catalogue\)/

/** Extract the rejected code from the description marker. */
function extractRejectedCode(text: string): string | null {
  const match = text.match(REJECTED_MARKER)
  return match ? match[1] : null
}

/** Enough shared words to be confident the two rows describe the same line. Tolerance for
 * A1's additions like "(customer's unit: DRUMS)" requires a loose match, but we must FAIL CLOSED
 * on non-Latin text (Arabic, numeric-only): both yield no comparable words and we cannot verify
 * those pairings. Dropping a real suggestion costs a manual match; offering the wrong line's code
 * costs a wrong order. */
function textsCorrespond(a: string, b: string): boolean {
  const words = (t: string) =>
    new Set(
      t
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 2),
    )
  const left = words(a)
  const right = words(b)
  // Fail closed: if either side yields nothing to compare, we cannot verify the pairing.
  if (left.size === 0 || right.size === 0) return false
  let shared = 0
  for (const w of left) if (right.has(w)) shared++
  return shared >= Math.min(2, Math.min(left.size, right.size))
}

export function buildSuggestions(lines: EnquiryItem[], extracted: ExtractedLine[]): Map<string, LineSuggestion> {
  const map = new Map<string, LineSuggestion>()
  if (lines.length === 0 || extracted.length === 0) return map

  for (const line of lines) {
    // `line_id` IS `enquiry_row_no` (see `toEnquiryItem`). Index is one less than the row number.
    const index = Number(line.line_id) - 1
    const source = Number.isInteger(index) && index >= 0 ? extracted[index] : undefined
    if (!source) continue

    // If text doesn't correspond, add empty suggestions for this line
    if (!textsCorrespond(line.raw_text, source.description)) {
      map.set(line.line_id, {
        itemCode: null,
        itemCodeRejected: false,
        candidates: [],
        // Nothing from the matcher is merged here yet — `mergeMatchIntoSuggestion` sets this.
        matchLowConfidence: false,
        customerUom: null,
      })
      continue
    }

    const rejectedCodeInSource = extractRejectedCode(source.description)
    const rejectedCodeInLine = extractRejectedCode(line.raw_text)
    const isRejected = rejectedCodeInSource !== null || rejectedCodeInLine !== null

    // Offer item_code when:
    // (1) persisted has none AND extraction has a real one, OR
    // (2) persisted has none AND extraction's code was rejected (preserved in description)
    let suggestionCode: string | null = null
    if (line.item_code === null) {
      if (source.item_code !== null) {
        // Real code from extraction
        suggestionCode = source.item_code
      } else if (rejectedCodeInSource !== null) {
        // Rejected code, recovered from description
        suggestionCode = rejectedCodeInSource
      } else if (rejectedCodeInLine !== null) {
        // Rejected code, recovered from persisted line's description
        suggestionCode = rejectedCodeInLine
      }
    }

    map.set(line.line_id, {
      itemCode: suggestionCode,
      itemCodeRejected: isRejected,
      // Safe to apply directly — see the field's own comment. Offered even where `itemCode` is
      // null, which is the case they exist for.
      candidates: source.candidates ?? [],
      // The extraction's own code is never a similarity score, so it is never flagged here.
      // `mergeMatchIntoSuggestion` sets this when the matcher's hit is what ends up proposed.
      matchLowConfidence: false,
      // Shown only when the extraction could NOT verify the unit — `raw_uom` is populated by
      // `verifyUoms` precisely then. Display only: see the field's own comment on `LineSuggestion`
      // for why this can never be a one-click apply.
      customerUom: source.uom === null && source.raw_uom ? source.raw_uom : null,
    })
  }

  return map
}

/**
 * How many suggestions "Accept all" can actually apply, and how many need a human instead.
 *
 * A rejected item code counts toward `needsMatching`, never `applicable` — the button must not
 * offer to apply something the server would refuse. `customerUom` counts toward neither: it is
 * display-only and never applicable, and it is not something the purchaser needs to "match" —
 * they already see it, plain text, beside the UOM select.
 */
export function countOutstanding(map: Map<string, LineSuggestion>): {
  applicable: number
  needsMatching: number
  /** How many of `applicable` are weak matches. Reported after a bulk apply — see below. */
  lowConfidence: number
} {
  let applicable = 0
  let needsMatching = 0
  let lowConfidence = 0
  for (const s of map.values()) {
    if (s.itemCode !== null) {
      if (s.itemCodeRejected) needsMatching++
      else {
        applicable++
        if (s.matchLowConfidence) lowConfidence++
      }
    }
  }
  return { applicable, needsMatching, lowConfidence }
}

/**
 * The extraction's answer and the matcher's, resolved into one proposal per line.
 *
 * Two independent sources can each offer a code. The extraction read the customer's mail and may
 * have found a code they actually WROTE; `bosun-search` ranks catalogue items by similarity to
 * the line's text and always offers its best. Where both have something, the written code wins —
 * a similarity score is a guess about what they meant, and a code is what they said.
 *
 * Candidates are taken from the matcher whenever it returned any, for matched and unmatched lines
 * alike: the runner-up list is what a purchaser checks a borderline match against.
 *
 * Lived in `RfqPage.tsx` until the low-confidence flag gave it a decision worth pinning; pure, so
 * it belongs beside `buildSuggestions` where both the screen and the tests read one copy.
 */
export function mergeMatchIntoSuggestion(
  suggestion: LineSuggestion | undefined,
  match: ItemMatch | undefined,
): LineSuggestion | undefined {
  if (!match) return suggestion
  const base: LineSuggestion = suggestion ?? {
    itemCode: null,
    itemCodeRejected: false,
    candidates: [],
    matchLowConfidence: false,
    customerUom: null,
  }
  const fromMatcher = base.itemCode === null && match.item_code !== null
  return {
    ...base,
    itemCode: base.itemCode ?? match.item_code,
    candidates: match.candidates.length > 0 ? match.candidates : base.candidates,
    // Only when the matcher's hit is the one being proposed — see the field's own comment.
    matchLowConfidence: fromMatcher && match.lowConfidence,
  }
}


/**
 * What the CONF. column may honestly claim about a line's matched item.
 *
 * ---- The bug this closes ----
 *
 * The column read `EnquiryItem.confidence`, which `useMailExtraction.ts` stamps as `1` on every
 * line it parses. That number means "the text was read", not "the item is right". Once
 * `AutoMatcher` began setting items from the matcher, a line matched at 0.03 rendered a full
 * green 100% bar directly beside the words "low confidence" — the column contradicting the label
 * next to it, and telling the purchaser the opposite of the truth.
 *
 * ---- The rule ----
 *
 * When the line carries the MATCHER's top pick, the matcher's own score is the honest number: it
 * measures the thing the column claims to measure. When the item came from anywhere else — the
 * purchaser chose it in the picker, or the customer wrote the code themselves — the matcher's
 * opinion of some other item says nothing about it, and the line's own value stands.
 *
 * `null` for an unmatched line: there is no item to be confident in. The caller shows the
 * candidate's score separately, labelled "not yet applied to this line".
 */
export function displayConfidence(
  line: { item_code: string | null; confidence: number },
  suggestion: LineSuggestion | undefined,
): number | null {
  if (!line.item_code) return null
  const best = suggestion?.candidates?.[0]
  // Only when the applied code IS the matcher's pick, and the matcher actually scored it.
  if (best && best.item_code === line.item_code && typeof best.score === 'number') return best.score
  return line.confidence
}
